<template>
  <div>
    <el-drawer
      custom-class="drawer-crossCheckAiOcr"
      :visible="isVisible"
      @update:visible="handleUpdateVisible"
      :close-on-press-escape="false"
      :before-close="handleClose"
      :with-header="false"
      direction="btt"
      :size="drawerSize"
    >
      <!-- 貿易書類照合前画面 -->
      <CrossCheckAiOcrList
        v-if="currentView === 'list'"
        :apiData="apiData"
        :loading="apiLoading"
        :isComparing="isComparing"
        :ocrStartLoading="ocrStartLoading"
        @refresh="handleRefresh"
        @compare-start="handleCompareStart"
        @compare-multi-start="handleCompareMultiStart"
        @preview-pdf="handlePreviewPdf"
        @start-ai-ocr="handleStartAiOcr"
        @close="handleClose"
        @save="(action, documents) => $emit('save', action, documents)"
      />

      <!-- 貿易書類比較の詳細画面 -->
      <CrossCheckAiOcrDetail
        v-if="currentView === 'detail'"
        :selectedDocuments="selectedDocuments"
        :compareData="compareData"
        :loading="compareLoading"
        @close="handleBackToList"
      />

      <!-- 貿易書類比較の詳細画面（複数書類） -->
      <CrossCheckAiOcrMultiDetail
        v-if="currentView === 'multiDetail'"
        :selectedDocuments="selectedDocuments"
        :compareData="compareData"
        :loading="compareLoading"
        @close="handleBackToList"
      />
    </el-drawer>

    <!-- ローディングオーバーレイ（ドロワー表示中の全画面カバー） -->
    <div v-if="isComparing && isVisible" class="compare-loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner">
          <i class="el-icon-loading" style="font-size: 48px; color: #409eff;"></i>
        </div>
        <div class="loading-message">{{ currentProgressMessage }}</div>
        <el-progress
          :percentage="progressPercentage"
          :show-text="false"
          status="success"
          :stroke-width="8"
        />
      </div>
    </div>

    <!-- PDFプレビュー -->
    <TwPdfViewer
      v-if="pdfSrc"
      :src="pdfSrc"
      @close-pdf-viewer="closePdfViewer"
    />
  </div>
</template>

<script>
import TwPdfViewer from '@/components/templates/TwPdfViewer.vue';
import { useBffSearchFlowDocument } from '@/composables/useBffSearchFlowDocument';
import { $api } from '@/store/ApiClient';
import { notify } from '@/libs/notify';
import sampleCompareResponse from '@/sampleData/postOcrCompare_response_sample.json';
import CrossCheckAiOcrList from './crossCheckAiOcrList.vue';
import CrossCheckAiOcrDetail from './crossCheckAiOcrDetail.vue';
import CrossCheckAiOcrMultiDetail from './crossCheckAiOcrMultiDetail.vue';

export default {
  name: 'CrossCheckAiOcr',
  components: {
    TwPdfViewer,
    CrossCheckAiOcrList,
    CrossCheckAiOcrDetail,
    CrossCheckAiOcrMultiDetail,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    tradingId: {
      type: String,
      default: 'T202601190000007', // サンプルデータのtradingId
    },
    tradingFlowId: {
      type: String,
      default: '1',
    },
  },
  setup(props) {
    // BFF APIを使用してドキュメントリストを取得
    const { data, loading, error, refresh } = useBffSearchFlowDocument({
      initialQueries: {
        tradingId: props.tradingId,
        tradingFlowId: props.tradingFlowId,
        limit: 100,
        offset: 0,
        sort: {
          target: ['processName'],
          sortType: ['ASC'],
        },
      },
      immediate: true, // すぐにデータ取得
    });

    return {
      apiData: data,
      apiLoading: loading,
      apiError: error,
      refreshData: refresh,
    };
  },
  data() {
    return {
      selectedDocuments: [],
      // AI-OCR読取開始のローディング状態（ドキュメントIDごと）
      ocrStartLoading: {},
      // PDFプレビュー用
      pdfSrc: null,
      // ドロワーのサイズ（初期値94%、比較開始時に100%）
      drawerSize: '94%',
      // 現在表示中のビュー（'list', 'detail', 'multiDetail'）
      currentView: 'list',
      // 比較データ
      compareData: null,
      // 比較データ読み込み中フラグ
      compareLoading: false,
      // 比較処理中フラグ（ローディングオーバーレイ表示用）
      isComparing: false,
      // 進捗メッセージ
      currentProgressMessage: '',
      // 進捗パーセンテージ
      progressPercentage: 0,
      // モック切り替えフラグ（true: サンプルデータ使用、false: 本番API）
      isMock: false,
    };
  },
  computed: {
    isVisible() {
      return this.visible;
    },
  },
  watch: {
    // ドロワーが開かれた時にデータを更新
    visible: {
      handler(newValue, oldValue) {
        // ドロワーが開かれた時（false → true）にデータを更新
        if (newValue && !oldValue) {
          // console.log('ドロワーが開かれました。データを更新します');
          this.refreshData();
        }
      },
    },
    apiLoading: {
      handler() {
        // console.log('API Loading状態:', loading);
      },
    },
    apiError: {
      handler(error) {
        if (error) {
          console.error('ドキュメント取得エラー:', error);
          this.$message.error('ドキュメントの取得に失敗しました');
        }
      },
    },
  },
  methods: {
    handleUpdateVisible(value) {
      this.$emit('update:visible', value);
    },
    handleClose() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    handleRefresh() {
      // console.log('ドキュメントリストを更新します');
      this.refreshData();
    },
    // PDFプレビューを開く
    async handlePreviewPdf(payload) {
      const row = payload.row || payload; // 互換性のため、古い形式もサポート
      const viewMode = payload.viewMode || 'newTab'; // デフォルトは別タブ

      // console.log('PDFプレビュー:', row, 'viewMode:', viewMode);

      // 元のAPIデータから完全なドキュメント情報を取得
      const fullDoc = this.apiData?.attachFileList?.find(
        (item) => item.documentManageId === row.documentManageId,
      );

      if (!fullDoc) {
        this.$message.error('ドキュメント情報が見つかりません');
        return;
      }

      try {
        // $api.downloadを使用してPDFをダウンロード
        const path = {
          documentManageId: fullDoc.documentManageId,
          documentTrx: fullDoc.documentTrx,
        };

        const src = await $api.download(
          path,
          fullDoc.documentName,
          null,
          null,
          false,
        );

        if (viewMode === 'newTab') {
          // 別タブで開く
          window.open(src, '_blank');
        } else {
          // プレビューで開く
          this.pdfSrc = src;
        }
      } catch (err) {
        console.error('PDFダウンロードエラー:', err);
        this.$message.error('PDFの取得に失敗しました');
        this.pdfSrc = null;
      }
    },
    // PDFプレビューを閉じる
    closePdfViewer() {
      this.pdfSrc = null;
    },
    // 比較開始ボタン押下時の処理
    async handleCompareStart(selectedDocuments) {
      // console.log('比較開始:', selectedDocuments);

      this.isComparing = true;
      this.progressPercentage = 0;

      try {
        // Step 1: OCRステータス取得（最新のdocumentTrxを取得）
        this.currentProgressMessage = '書類情報を取得しています...';
        this.progressPercentage = 30;

        const documentList = selectedDocuments.map((doc) => ({
          documentManageId: doc.documentManageId,
        }));

        const ocrParams = {
          lslConfig: {
            serviceCode: 'tw-pooldata-bff-api',
            apiCode: 'post_/v1/AiOcr/getAiOcrStatus',
          },
          data: {
            getTargetList: documentList,
          },
        };

        const ocrRes = await $api.request(ocrParams);
        const ocrStatusList = ocrRes?.ocrStatusList || [];

        // selectedOcrDocuments を作成（最新のdocumentTrxを含む）
        const selectedOcrDocuments = selectedDocuments.map((doc) => {
          const ocrData = ocrStatusList.find(
            (ocr) => ocr.documentManageId === doc.documentManageId,
          );
          return {
            ...doc,
            documentTrx: ocrData?.documentTrx || doc.documentTrx,
          };
        });

        this.selectedDocuments = selectedOcrDocuments;

        // Step 2: 比較データ取得
        this.currentProgressMessage = '比較データを読み込んでいます...';
        this.progressPercentage = 70;

        let compareRes;

        if (this.isMock) {
          // モード: サンプルデータを使用
          console.log('モックモード: サンプルデータを使用');
          await this.sleep(500); // API呼び出しをシミュレート
          compareRes = sampleCompareResponse;
        } else {
          // 本番モード: 実際のBFF APIを呼び出し
          const documents = selectedOcrDocuments.map((doc) => ({
            documentManageId: doc.documentManageId,
            documentTrx: doc.documentTrx,
          }));

          const compareParams = {
            lslConfig: {
              serviceCode: 'tw-transaction-bff-api',
              apiCode: 'post_/v1/AiOcr/postOcrCompare',
            },
            data: {
              documents: documents,
            },
          };

          // console.log('本番モード: 比較API呼び出し', compareParams);
          compareRes = await $api.request(compareParams);
        }

        this.compareData = compareRes;
        // console.log('比較データ取得完了:', compareRes);

        // Step 3: 完了
        this.currentProgressMessage = '比較結果を準備しています...';
        this.progressPercentage = 100;
        await this.sleep(300);

        // ドロワーを全画面表示に変更
        this.drawerSize = '100%';
        // 詳細画面に切り替え
        this.currentView = 'detail';
      } catch (error) {
        console.error('比較処理エラー:', error);
        this.$message.error(
          error.message || '比較データの取得に失敗しました',
        );
      } finally {
        this.isComparing = false;
        this.compareLoading = false;
      }
    },
    async handleCompareMultiStart(selectedDocuments) {
      this.isComparing = true;
      this.progressPercentage = 0;

      try {
        // Step 1: OCRステータス取得
        this.currentProgressMessage = '書類情報を取得しています...';
        this.progressPercentage = 20;

        const documentList = selectedDocuments.map((doc) => ({
          documentManageId: doc.documentManageId,
        }));

        const ocrParams = {
          lslConfig: {
            serviceCode: 'tw-pooldata-bff-api',
            apiCode: 'post_/v1/AiOcr/getAiOcrStatus',
          },
          data: {
            getTargetList: documentList,
          },
        };

        const ocrRes = await $api.request(ocrParams);
        const ocrStatusList = ocrRes?.ocrStatusList || [];

        const selectedOcrDocuments = selectedDocuments.map((doc) => {
          const ocrData = ocrStatusList.find(
            (ocr) => ocr.documentManageId === doc.documentManageId,
          );
          return {
            ...doc,
            documentTrx: ocrData?.documentTrx || doc.documentTrx,
          };
        });

        this.selectedDocuments = selectedOcrDocuments;

        // Step 2: 比較データ取得
        this.currentProgressMessage = `${selectedDocuments.length}件の書類を比較しています...`;
        this.progressPercentage = 60;

        let compareRes;

        if (this.isMock) {
          console.log('モックモード: サンプルデータを使用');
          await this.sleep(1000);
          compareRes = sampleCompareResponse;
        } else {
          const documents = selectedOcrDocuments.map((doc) => ({
            documentManageId: doc.documentManageId,
            documentTrx: doc.documentTrx,
          }));

          const compareParams = {
            lslConfig: {
              serviceCode: 'tw-transaction-bff-api',
              apiCode: 'post_/v1/AiOcr/postOcrCompare',
            },
            data: {
              documents: documents,
            },
          };

          compareRes = await $api.request(compareParams);
        }

        this.compareData = compareRes;

        // Step 3: 完了
        this.currentProgressMessage = '比較結果を準備しています...';
        this.progressPercentage = 100;
        await this.sleep(300);

        this.drawerSize = '100%';
        this.currentView = 'multiDetail';
      } catch (error) {
        console.error('複数書類比較処理エラー:', error);
        this.$message.error(
          error.message || '複数書類比較データの取得に失敗しました',
        );
      } finally {
        this.isComparing = false;
        this.compareLoading = false;
      }
    },
    // 指定ミリ秒待機する
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    // 詳細画面から照合前画面に戻る
    handleBackToList() {
      // console.log('照合前画面に戻ります');
      this.currentView = 'list';
      // ドロワーサイズを90%に戻す
      this.drawerSize = '90%';
      // 比較データをクリア
      this.compareData = null;
      this.selectedDocuments = [];
    },
    // AI-OCR読取開始処理
    async handleStartAiOcr(row) {
      // 元のAPIデータから完全なドキュメント情報を取得
      const fullDoc = this.apiData?.attachFileList?.find(
        item => item.documentManageId === row.documentManageId
      );

      if (!fullDoc) {
        this.$message.error('ドキュメント情報が見つかりません');
        return;
      }

      // 帳票区分のカウント
      const documentLinkCount = [
        fullDoc.documentLinkInv,
        fullDoc.documentLinkPl,
        fullDoc.documentLinkBl,
        fullDoc.documentLinkAn,
        fullDoc.documentLinkSi,
        fullDoc.documentLinkCo,
      ].filter(value => value === 1).length;

      // 2つ以上が1の場合、エラーを投げる
      if (documentLinkCount > 1) {
        this.$message.error('帳票区分が複数ある場合、AI-OCRは実行できません。');
        return;
      }

      // 確認ダイアログ
      try {
        await this.$confirm('AI-OCRでのPDFファイルの読取を開始します。', '確認', {
          confirmButtonText: 'OK',
          cancelButtonText: 'キャンセル',
          type: 'info',
        });
      } catch {
        return; // キャンセルされた
      }

      const profile = this.$store.state.userProfile;

      // documentTypeIdを決定
      let documentTypeId = '';
      if (fullDoc.documentLinkInv === 1) documentTypeId = '1';
      else if (fullDoc.documentLinkPl === 1) documentTypeId = '2';
      else if (fullDoc.documentLinkBl === 1) documentTypeId = '3';
      else if (fullDoc.documentLinkAn === 1) documentTypeId = '4';
      else if (fullDoc.documentLinkSi === 1) documentTypeId = '5';
      else if (fullDoc.documentLinkCo === 1) documentTypeId = '6';
      else if (fullDoc.documentLinkDn === 1) documentTypeId = '8';
      else if (fullDoc.documentLinkPo === 1) documentTypeId = '9';

      const data = {
        documentTrx: fullDoc.documentTrx,
        documentTypeId: documentTypeId,
        documentManageId: fullDoc.documentManageId,
        processTradingInfo: {
          tradingId: fullDoc.tradingId,
          tradingFlowId: fullDoc.tradingFlowId,
          entityId: fullDoc.entityId,
          processId: fullDoc.processId,
          processSeq: fullDoc.processSeq,
          companyId: profile.companyId,
          requestUserId: profile.userId,
        },
      };

      const params = {
        lslConfig: {
          serviceCode: 'tw-pooldata-bff-api',
          apiCode: 'post_/v1/AiOcr',
        },
        data: data,
      };

      // ローディング開始
      this.$set(this.ocrStartLoading, row.documentManageId, true);

      try {
        await $api.request(params);

        notify.success({
          title: 'AI-OCRでの読取を開始しました',
          message: '画面を更新し、AI-OCR: 結果確認ボタンより、取得状況をご確認ください。完了まで約1-2分ほど時間がかかります。',
        });

        // データを再取得
        await this.refreshData();
      } catch (error) {
        console.error('AI-OCR開始エラー:', error);
        this.$message.error('AI-OCRの開始に失敗しました: ' + (error.message || ''));
      } finally {
        this.$set(this.ocrStartLoading, row.documentManageId, false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// スタイルは子コンポーネントに移動
</style>

<style lang="scss">
// PDFプレビューをdrawerより上に表示
.pdf_viewer {
  z-index: 3000 !important;
}

.drawer-crossCheckAiOcr {
  display: flex;
  flex-direction: column;
  // サイズ変更時のアニメーション（90% ⇔ 100%）
  transition: height 0.4s!important;

  .el-drawer__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;
  }
}

// ローディングオーバーレイ（ビューポート全体をカバー）
.compare-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999 !important;

  .loading-content {
    text-align: center;
    width: 400px;
    padding: 40px;

    .loading-spinner {
      margin-bottom: 24px;
    }

    .loading-message {
      font-size: 16px;
      color: #606266;
      margin-bottom: 24px;
      font-weight: 500;
      min-height: 24px;
    }
  }
}
</style>
